import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Skeleton, Stack } from '@mui/material';

import { BAD_STATION_STATUS_PAGE_PATH } from '~configs';
import { useNavigateInApp } from '~hooks';
import { useQueryGetOngoingRental } from '~hooks/useQueryGetOngoingRental';
import { inMemoryJWTService } from '~services/jwt.service';

const ONGOING_RENTAL_CHECK_TIMEOUT = 3000; // 3 seconds

export const OngoingRentalGuard: FC<PropsWithChildren> = ({ children }) => {
  const navigate = useNavigateInApp();
  const location = useLocation();
  const [isCheckingOngoingRental, setIsCheckingOngoingRental] = useState(true);

  const shouldSkipGuard = useMemo(
    () => location.pathname.startsWith(BAD_STATION_STATUS_PAGE_PATH),
    [location.pathname],
  );

  const { isFetching } = useQueryGetOngoingRental({
    enabled: !!inMemoryJWTService.getCurrentToken() && !shouldSkipGuard,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (data?.rental) {
        navigate(`${BAD_STATION_STATUS_PAGE_PATH}/alreadyRented`, {
          replace: true,
        });
      }
    },
  });

  useEffect(() => {
    if (shouldSkipGuard) {
      setIsCheckingOngoingRental(false);
      return;
    }

    const timer = window.setTimeout(() => {
      setIsCheckingOngoingRental(false);
    }, ONGOING_RENTAL_CHECK_TIMEOUT);

    return () => {
      window.clearTimeout(timer);
    };
  }, [shouldSkipGuard]);

  if (isCheckingOngoingRental || isFetching) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rectangular" height={100} />
        <Skeleton variant="rectangular" height={100} />
        <Skeleton variant="rectangular" height={100} />
      </Stack>
    );
  }

  return <>{children}</>;
};
